'use client';

import { useCallback } from 'react';
import { useBrowserState } from './use-browser-state';

const RECENT_KEY = 'kontora-sidebar-recent';
const FAVORITES_KEY = 'kontora-sidebar-favorites';
const COLLAPSED_GROUPS_KEY = 'kontora-sidebar-collapsed';
const MAX_RECENT = 8;
const EMPTY_RECENT: RecentEntry[] = [];
const EMPTY_FAVORITES: string[] = [];
const EMPTY_GROUPS = new Set<string>();

export interface RecentEntry {
  href: string;
  label: string;
  context: string;
  visitedAt: number;
}

export interface FavoritesState {
  favoriteIds: string[];
}

function safeGet(key: string): string | null {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeSet(key: string, value: string): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // localStorage might be unavailable
  }
  window.dispatchEvent(new Event('kontora:browser-state-change'));
}

export function readRecentEntries(): RecentEntry[] {
  const raw = safeGet(RECENT_KEY);
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw) as RecentEntry[];
    if (!Array.isArray(parsed)) return [];
    return parsed.slice(0, MAX_RECENT);
  } catch {
    return [];
  }
}

export function writeRecentEntries(entries: RecentEntry[]): void {
  safeSet(RECENT_KEY, JSON.stringify(entries.slice(0, MAX_RECENT)));
}

export function recordPageVisit(href: string, label: string, context: string): void {
  if (typeof window === 'undefined') return;
  if (!href || href.startsWith('#')) return;
  if (href === '/login' || href === '/') return;
  const current = readRecentEntries();
  const filtered = current.filter((entry) => entry.href !== href);
  const next: RecentEntry[] = [
    { href, label, context, visitedAt: Date.now() },
    ...filtered,
  ].slice(0, MAX_RECENT);
  writeRecentEntries(next);
}

export function clearRecentEntries(): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.removeItem(RECENT_KEY);
  } catch {
    // ignore
  }
}

export function readFavorites(): string[] {
  const raw = safeGet(FAVORITES_KEY);
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw) as string[];
    if (!Array.isArray(parsed)) return [];
    return parsed;
  } catch {
    return [];
  }
}

export function writeFavorites(ids: string[]): void {
  safeSet(FAVORITES_KEY, JSON.stringify(ids));
}

export function toggleFavorite(id: string): boolean {
  const current = readFavorites();
  const isFavorite = current.includes(id);
  const next = isFavorite ? current.filter((fav) => fav !== id) : [...current, id];
  writeFavorites(next);
  return !isFavorite;
}

export function isFavorite(id: string): boolean {
  return readFavorites().includes(id);
}

export function readCollapsedGroups(): Set<string> {
  const raw = safeGet(COLLAPSED_GROUPS_KEY);
  if (!raw) return new Set();
  try {
    const arr = JSON.parse(raw) as string[];
    if (!Array.isArray(arr)) return new Set();
    return new Set(arr);
  } catch {
    return new Set();
  }
}

export function writeCollapsedGroups(collapsed: Set<string>): void {
  safeSet(COLLAPSED_GROUPS_KEY, JSON.stringify([...collapsed]));
}

export interface FavoriteTarget {
  id: string;
  href: string;
  label: string;
  context: string;
}

export function buildFavoritesFromNavigation(
  items: ReadonlyArray<{ label: string; href?: string; context: string }>,
): FavoriteTarget[] {
  return items
    .filter((item): item is { label: string; href: string; context: string } => Boolean(item.href))
    .map((item) => ({
      id: `${item.context}::${item.href}`,
      href: item.href,
      label: item.label,
      context: item.context,
    }));
}

export interface RecentNavItem {
  href: string;
  label: string;
  context: string;
  visitedAt: number;
}

export function useRecentEntries() {
  const [entries, setEntries] = useBrowserState(readRecentEntries, EMPTY_RECENT);

  const refresh = useCallback(() => {
    setEntries(readRecentEntries());
  }, [setEntries]);

  const record = useCallback((href: string, label: string, context: string) => {
    recordPageVisit(href, label, context);
    setEntries(readRecentEntries());
  }, [setEntries]);

  const clear = useCallback(() => {
    clearRecentEntries();
    setEntries([]);
  }, [setEntries]);

  return { entries, refresh, record, clear };
}

export function useFavorites() {
  const [favorites, setFavorites] = useBrowserState(readFavorites, EMPTY_FAVORITES);

  const refresh = useCallback(() => {
    setFavorites(readFavorites());
  }, [setFavorites]);

  const toggle = useCallback((id: string) => {
    const next = toggleFavorite(id);
    setFavorites(readFavorites());
    return next;
  }, [setFavorites]);

  const has = useCallback(
    (id: string) => favorites.includes(id),
    [favorites],
  );

  return { favorites, refresh, toggle, has };
}

export function useCollapsedGroups() {
  const [collapsed, setCollapsed] = useBrowserState(readCollapsedGroups, EMPTY_GROUPS);

  const toggle = useCallback((id: string) => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      writeCollapsedGroups(next);
      return next;
    });
  }, [setCollapsed]);

  const expandAll = useCallback(() => {
    const empty = new Set<string>();
    writeCollapsedGroups(empty);
    setCollapsed(empty);
  }, [setCollapsed]);

  const collapseAll = useCallback((ids: string[]) => {
    const all = new Set(ids);
    writeCollapsedGroups(all);
    setCollapsed(all);
  }, [setCollapsed]);

  return { collapsed, toggle, expandAll, collapseAll };
}

export const __TESTING = {
  RECENT_KEY,
  FAVORITES_KEY,
  COLLAPSED_GROUPS_KEY,
  MAX_RECENT,
};
